import i18n, { INTL_LOCALE, isAppLocale, type AppLocale } from '@/i18n'

function resolveLocale(locale?: string): AppLocale {
  const candidate = locale ?? i18n.language
  return isAppLocale(candidate) ? candidate : 'en'
}

/** Stablecoins / crypto tickers that are not ISO 4217. */
const NON_ISO_CURRENCY = new Set([
  'USDT',
  'USDC',
  'BUSD',
  'TUSD',
  'DAI',
  'BTC',
  'ETH',
  'SOL',
  'BNB',
  'XAU',
  'XAG',
])

function isIsoCurrency(code: string): boolean {
  if (!code || code.length !== 3) return false
  if (NON_ISO_CURRENCY.has(code.toUpperCase())) return false
  try {
    // Throws RangeError for invalid ISO 4217 codes
    new Intl.NumberFormat('en', { style: 'currency', currency: code }).format(0)
    return true
  } catch {
    return false
  }
}

/**
 * Locale-aware number formatting for prices, sizes, PnL, etc.
 */
export function formatNumber(
  value: number,
  options?: Intl.NumberFormatOptions,
  locale?: string,
): string {
  const tag = INTL_LOCALE[resolveLocale(locale)]
  const { style, currency, ...rest } = options ?? {}

  // Guard: never pass a non-ISO currency into Intl currency style
  if (style === 'currency' && currency && !isIsoCurrency(currency)) {
    const digits =
      Math.abs(value) < 1 ? 4 : Math.abs(value) < 100 ? 4 : 2
    const formatted = new Intl.NumberFormat(tag, {
      maximumFractionDigits: digits,
      minimumFractionDigits: Math.min(2, digits),
      ...rest,
    }).format(value)
    return `${formatted} ${currency.toUpperCase()}`
  }

  return new Intl.NumberFormat(tag, {
    maximumFractionDigits: 2,
    ...options,
  }).format(value)
}

/**
 * Financial currency formatting.
 * ISO 4217 (USD, EUR, …) uses native currency style;
 * crypto / quote tickers (USDT, BTC, …) append the code after a numeric format.
 */
export function formatCurrency(
  value: number,
  currency = 'USD',
  locale?: string,
): string {
  const code = currency.trim().toUpperCase() || 'USD'
  return formatNumber(
    value,
    {
      style: 'currency',
      currency: code,
      currencyDisplay: 'symbol',
    },
    locale,
  )
}

/**
 * Compact quote size (e.g. 1.2M) for dense order-book / watchlist cells.
 */
export function formatCompact(
  value: number,
  locale?: string,
): string {
  return formatNumber(
    value,
    {
      notation: 'compact',
      maximumFractionDigits: 1,
    },
    locale,
  )
}

/**
 * Signed percent change with explicit + / − for scanability.
 */
export function formatPercentChange(
  value: number,
  locale?: string,
): string {
  return formatNumber(
    value,
    {
      style: 'percent',
      signDisplay: 'exceptZero',
      maximumFractionDigits: 2,
    },
    locale,
  )
}
